/**
 * Iconografía de navegación: trazo, 24×24, `currentColor`.
 *
 * En línea y no como dependencia. Catorce iconos no justifican un paquete de
 * mil, y sobre todo: heredan el color del enlace, de modo que el estado activo
 * —fondo navy, tinta blanca— tiñe el icono sin una regla aparte. Un set
 * empaquetado con `fill` propio habría obligado a sobreescribirlo en cada
 * estado.
 *
 * `aria-hidden`: el icono acompaña a un rótulo de texto, nunca lo sustituye.
 * Con el menú plegado el rótulo sigue existiendo —en `title` y en
 * `aria-label`—, así que el icono nunca es el único portador del significado.
 */

export type IconName =
  | "panel"
  | "estados"
  | "balance"
  | "diario"
  | "mayor"
  | "compras"
  | "ventas"
  | "anexos"
  | "corridas"
  | "excepciones"
  | "auditoria"
  | "membrete"
  | "ajustes"
  | "plegar";

const PATHS: Record<IconName, string> = {
  panel: "M4 4h6v6H4zM14 4h6v4h-6zM14 12h6v8h-6zM4 14h6v6H4z",
  estados: "M14 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7zM14 3v4h4M9 13h6M9 17h4",
  balance: "M12 4v16M6 20h12M3 9h18M6 9l-3 5a3 3 0 0 0 6 0zM18 9l3 5a3 3 0 0 1-6 0z",
  diario: "M5 5a2 2 0 0 1 2-2h12v18H7a2 2 0 0 0-2 2zM9 8h7M9 12h5",
  mayor: "M12 7S9.5 4.5 4 4.5v14c5.5 0 8 2.5 8 2.5s2.5-2.5 8-2.5v-14C14.5 4.5 12 7 12 7zM12 7v14",
  compras: "M4 14v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5M12 3v11M8 10l4 4 4-4",
  ventas: "M4 14v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5M12 14V3M8 7l4-4 4 4",
  anexos:
    "M9 3h6v3H9zM9 4.5H7a1 1 0 0 0-1 1V20a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V5.5a1 1 0 0 0-1-1h-2M9 12h6M9 16h4",
  corridas: "M3.5 12a8.5 8.5 0 0 1 14.4-6.1L21 8M21 4v4h-4M20.5 12a8.5 8.5 0 0 1-14.4 6.1L3 16M3 20v-4h4",
  excepciones: "M12 3.5 2.8 20h18.4zM12 10v4.5M12 17.5h.01",
  auditoria: "M12 3l8 3v6c0 5-3.4 8.2-8 9.2C7.4 20.2 4 17 4 12V6zM9 12l2.2 2.2L15.5 10",
  membrete: "M4 20h4L20.2 7.8a2.7 2.7 0 0 0-3.8-3.8L4 16.2zM14.5 6.5l3 3M4 20l.6-3.8",
  ajustes:
    "M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4zM19.6 12a7.6 7.6 0 0 0-.1-1.2l2-1.5-2-3.4-2.3 1a7.6 7.6 0 0 0-2-1.2L14.8 3H9.2l-.4 2.7c-.7.3-1.4.7-2 1.2l-2.3-1-2 3.4 2 1.5a7.6 7.6 0 0 0 0 2.4l-2 1.5 2 3.4 2.3-1c.6.5 1.3.9 2 1.2l.4 2.7h5.6l.4-2.7c.7-.3 1.4-.7 2-1.2l2.3 1 2-3.4-2-1.5c.1-.4.1-.8.1-1.2z",
  plegar: "M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zM10 5v14",
};

export function Icon({ name, className }: { name: IconName; className?: string }) {
  return (
    <svg
      className={className ?? "ico"}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.65"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
